import React, { useState, useEffect, useRef } from 'react';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../../styles';

interface NewsOnChartsAnimationProps {
  isActive: boolean;
}

const popIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Spark chart path - smooth curves using quadratic bezier
// ViewBox: 0 0 200 100, chart area roughly 15-85 on Y axis
const CHART_PATH = 'M0,75 Q10,72 20,68 T48,55 T80,42 T110,25 T140,38 T170,22 T200,18';

// Closed path for gradient fill (same line + bottom edge)
const CHART_FILL_PATH = 'M0,75 Q10,72 20,68 T48,55 T80,42 T110,25 T140,38 T170,22 T200,18 L200,100 L0,100 Z';

// Markers positioned on the chart path - hollow circles like the real app
const NEWS_MARKERS = [
  { x: 48, y: 55, date: 'Jan 25', headline: 'Apple beats Q1 earnings, stock jumps 4.2%' },
  { x: 110, y: 25, date: 'Feb 1', headline: 'iPhone sales exceed expectations' },
  { x: 170, y: 22, date: 'Feb 10', headline: 'Services revenue hits record high' },
];

export const NewsOnChartsAnimation: React.FC<NewsOnChartsAnimationProps> = ({ isActive }) => {
  const [visibleMarkers, setVisibleMarkers] = useState<number[]>([]);
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    // Clear existing timeouts
    timeoutIdsRef.current.forEach((id) => clearTimeout(id));
    timeoutIdsRef.current = [];

    if (isActive) {
      setVisibleMarkers([]);
      setActiveTooltip(null);

      // Stagger marker appearances
      NEWS_MARKERS.forEach((_, index) => {
        const id = setTimeout(
          () => {
            setVisibleMarkers((prev) => [...prev, index]);
          },
          600 + index * 350,
        );
        timeoutIdsRef.current.push(id);
      });

      // Show tooltip on the first marker (index 0) - more room for positioning
      const tooltipId = setTimeout(() => {
        setActiveTooltip(0);
      }, 1800);
      timeoutIdsRef.current.push(tooltipId);
    }

    return () => {
      const ids = timeoutIdsRef.current;
      ids.forEach((id) => clearTimeout(id));
    };
  }, [isActive]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: ${spacing.deci};
        height: 100%;
        overflow: visible;
      `}
    >
      {/* Header */}
      <div
        css={css`
          margin-bottom: ${spacing.centi};
        `}
      ></div>

      {/* Chart area */}
      <div
        css={css`
          position: relative;
          flex: 1;
          min-height: 100px;
        `}
      >
        {/* SVG with chart line and markers */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 200 100"
          preserveAspectRatio="none"
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            overflow: visible;
          `}
        >
          {/* Gradient definition */}
          <defs>
            <linearGradient id="sparkGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={colors.leaf} stopOpacity="0.25" />
              <stop offset="100%" stopColor={colors.leaf} stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Gradient fill under the line */}
          <path d={CHART_FILL_PATH} fill="url(#sparkGradient)" />
          {/* Smooth spark line */}
          <path d={CHART_PATH} fill="none" stroke={colors.leaf} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>

        {/* Markers as DOM elements - hollow circles like real app */}
        {NEWS_MARKERS.map((marker, index) => {
          const isVisible = visibleMarkers.includes(index);
          // Convert SVG coords (200x100) to percentages
          const leftPercent = (marker.x / 200) * 100;
          const topPercent = (marker.y / 100) * 100;

          return (
            <div
              key={index}
              css={css`
                position: absolute;
                left: ${leftPercent}%;
                top: ${topPercent}%;
                transform: translate(-50%, -50%);
                z-index: ${activeTooltip === index ? 20 : 10};
              `}
            >
              {/* Marker - hollow circle like the real app */}
              <div
                css={css`
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  background: ${colors.sugar};
                  border: 2.5px solid ${colors.petrolBlue};
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
                  opacity: ${isVisible ? 1 : 0};
                  transform: scale(${isVisible ? 1 : 0});
                  animation: ${isVisible ? popIn : 'none'} 0.3s ease-out forwards;

                  html[data-theme='dark'] & {
                    background: ${colors.liquorice};
                    border-color: ${colors.skyBlue};
                  }
                `}
              />

              {/* Tooltip */}
              {activeTooltip === index && (
                <div
                  css={css`
                    position: absolute;
                    bottom: 100%;
                    left: -7px;
                    margin-bottom: 8px;
                    animation: ${fadeInUp} 0.3s ease-out forwards;
                  `}
                >
                  <div
                    css={css`
                      background: ${colors.sugar};
                      border: 1px solid ${colors.cloud};
                      border-radius: 10px;
                      padding: ${spacing.centi} ${spacing.deci};
                      min-width: 180px;
                      max-width: 200px;
                      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
                      white-space: normal;

                      html[data-theme='dark'] & {
                        background: ${colors.liquorice};
                        border-color: ${colors.tungsten};
                      }

                      &::after {
                        content: '';
                        position: absolute;
                        top: 100%;
                        left: 14px;
                        width: 2px;
                        height: 8px;
                        background: ${colors.petrolBlue};

                        html[data-theme='dark'] & {
                          background: ${colors.skyBlue};
                        }
                      }
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        margin-bottom: 4px;
                      `}
                    >
                      <span
                        css={css`
                          width: 8px;
                          height: 8px;
                          border-radius: 50%;
                          background: ${colors.petrolBlue};
                          flex-shrink: 0;

                          html[data-theme='dark'] & {
                            background: ${colors.skyBlue};
                          }
                        `}
                      />
                      <span
                        css={css`
                          ${fontStyles.flea};
                          color: ${colors.tungsten};

                          html[data-theme='dark'] & {
                            color: ${colors.silver};
                          }
                        `}
                      >
                        {marker.date}
                      </span>
                    </div>
                    <div
                      css={css`
                        ${fontStyles.mouse};
                        color: ${colors.liquorice};
                        line-height: 1.35;

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {marker.headline}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Time range tabs */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          gap: ${spacing.deci};
          padding-top: ${spacing.centi};
        `}
      >
        {['1D', '1W', '1M', '3M', '1Y'].map((range, i) => (
          <div
            key={range}
            css={css`
              ${fontStyles.flea};
              color: ${i === 2 ? colors.petrolBlue : colors.tungsten};
              padding: 2px 8px;
              border-radius: 4px;
              background: ${i === 2 ? colors.skyAir : 'transparent'};

              html[data-theme='dark'] & {
                color: ${i === 2 ? colors.skyBlue : colors.silver};
                background: ${i === 2 ? 'rgba(40, 181, 189, 0.2)' : 'transparent'};
              }
            `}
          >
            {range}
          </div>
        ))}
      </div>
    </div>
  );
};
